import { useCallback, useEffect } from 'react';
import { DraftSelectionInteraction, DraftTaskSelection } from './types';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

interface PointerHandlers {
    cancelPendingTouchCreate: () => void;
    expandTimelineWindowAtEdge: (container: HTMLDivElement) => void;
    getDraftSelectionForPointer: (interaction: DraftSelectionInteraction, clientY: number) => DraftTaskSelection;
    handleCreationPointerMove: (event: PointerEvent) => void;
    handleCreationPointerUp: () => void;
    handleDraftPointerMove: (event: PointerEvent) => void;
    handleDraftPointerUp: () => void;
    handleResizePointerMove: (event: PointerEvent) => void;
    handleResizePointerUp: (event: PointerEvent) => void;
    handleTaskPointerMove: (event: PointerEvent) => void;
    handleTaskPointerUp: (event: PointerEvent) => void;
    updateActiveTimelineLabel: () => void;
}

const AUTO_SCROLL_EDGE_SIZE = 48;
const MAX_AUTO_SCROLL_SPEED = 4;

export const useTimelinePointerEvents = (
    state: PlannerState, refs: PlannerRefs, handlers: PointerHandlers
) => {
    const {
        draftInteraction, draggingTask, isDragging, pendingTouchCreate, resizingTask,
        setCurrentMouseY, setDraftInteraction, setDraftSelection, setDraggingTask,
        setDragTaskStartTopY, setDragTaskStartY, setDragStartHour, setDragStartY,
        setHasDragged, setIsDragging, setResizeStartY, setResizingTask, setDragTaskScrollTop,
    } = state;
    const {
        dragAutoScrollActiveRef, dragAutoScrollFrameRef, dragAutoScrollPointerYRef,
        draftInteractionRef, timelineRef,
    } = refs;
    const stopDragAutoScroll = useCallback(() => {
        dragAutoScrollActiveRef.current = false;
        dragAutoScrollPointerYRef.current = null;
        if (dragAutoScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(dragAutoScrollFrameRef.current);
            dragAutoScrollFrameRef.current = null;
        }
    }, [dragAutoScrollActiveRef, dragAutoScrollFrameRef, dragAutoScrollPointerYRef]);
    const runDragAutoScroll = useCallback(() => {
        dragAutoScrollFrameRef.current = null;
        if (!dragAutoScrollActiveRef.current) return;
        const container = timelineRef.current;
        const pointerY = dragAutoScrollPointerYRef.current;
        if (!container || pointerY === null) {
            stopDragAutoScroll();
            return;
        }
        const rect = container.getBoundingClientRect();
        const controls = container.querySelector('.timeline-zoom-controls') as HTMLElement | null;
        const topBoundary = controls?.getBoundingClientRect().bottom || rect.top;
        let delta = 0;
        if (pointerY >= topBoundary && pointerY < topBoundary + AUTO_SCROLL_EDGE_SIZE) {
            const ratio = (topBoundary + AUTO_SCROLL_EDGE_SIZE - pointerY) / AUTO_SCROLL_EDGE_SIZE;
            delta = -Math.ceil(MAX_AUTO_SCROLL_SPEED * Math.min(1, ratio));
        } else if (pointerY > rect.bottom - AUTO_SCROLL_EDGE_SIZE) {
            const ratio = (pointerY - rect.bottom + AUTO_SCROLL_EDGE_SIZE) / AUTO_SCROLL_EDGE_SIZE;
            delta = Math.ceil(MAX_AUTO_SCROLL_SPEED * Math.min(1, ratio));
        }
        if (delta) {
            container.scrollTop += delta;
            if (draggingTask) setDragTaskScrollTop(container.scrollTop);
            setCurrentMouseY(pointerY);
            const interaction = draftInteractionRef.current;
            if (interaction?.mode === 'drag') {
                setDraftSelection(handlers.getDraftSelectionForPointer(interaction, pointerY));
            }
            handlers.updateActiveTimelineLabel();
            handlers.expandTimelineWindowAtEdge(container);
        }
        dragAutoScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
    }, [dragAutoScrollActiveRef, dragAutoScrollFrameRef, dragAutoScrollPointerYRef,
        draftInteractionRef, draggingTask, handlers, setCurrentMouseY, setDragTaskScrollTop, setDraftSelection,
        stopDragAutoScroll, timelineRef]);
    const updateDragAutoScroll = useCallback((clientY: number, enabled: boolean) => {
        if (!enabled) {
            stopDragAutoScroll();
            return;
        }
        dragAutoScrollPointerYRef.current = clientY;
        dragAutoScrollActiveRef.current = true;
        if (dragAutoScrollFrameRef.current === null) {
            dragAutoScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
        }
    }, [dragAutoScrollActiveRef, dragAutoScrollFrameRef, dragAutoScrollPointerYRef,
        runDragAutoScroll, stopDragAutoScroll]);
    const cancelActiveGesture = useCallback(() => {
        handlers.cancelPendingTouchCreate();
        setIsDragging(false);
        setDragStartHour(null);
        setDragStartY(null);
        setHasDragged(false);
        setDraggingTask(null);
        setDragTaskScrollTop(0);
        setDragTaskStartY(null);
        setDragTaskStartTopY(null);
        setResizingTask(null);
        setResizeStartY(null);
        setDraftInteraction(null);
        setCurrentMouseY(null);
    }, [handlers, setCurrentMouseY, setDraftInteraction, setDragTaskScrollTop, setDragTaskStartTopY,
        setDragTaskStartY, setDragStartHour, setDragStartY, setDraggingTask, setHasDragged,
        setIsDragging, setResizeStartY, setResizingTask]);
    useEffect(() => () => stopDragAutoScroll(), [stopDragAutoScroll]);
    useEffect(() => {
        const move = (event: PointerEvent) => {
            handlers.handleCreationPointerMove(event);
            handlers.handleTaskPointerMove(event);
            handlers.handleResizePointerMove(event);
            handlers.handleDraftPointerMove(event);
            updateDragAutoScroll(event.clientY, Boolean(draggingTask || draftInteraction?.mode === 'drag'));
        };
        const up = (event: PointerEvent) => {
            handlers.handleCreationPointerUp();
            handlers.handleTaskPointerUp(event);
            handlers.handleResizePointerUp(event);
            handlers.handleDraftPointerUp();
            stopDragAutoScroll();
        };
        const cancel = () => { cancelActiveGesture(); stopDragAutoScroll(); };
        if (isDragging || draggingTask || resizingTask || pendingTouchCreate || draftInteraction) {
            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
            document.addEventListener('pointercancel', cancel);
        }
        return () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            document.removeEventListener('pointercancel', cancel);
        };
    }, [cancelActiveGesture, draftInteraction, draggingTask, handlers, isDragging,
        pendingTouchCreate, resizingTask, stopDragAutoScroll, updateDragAutoScroll]);
};
